// Site-wide light / dark theme for keeptabs.app, matching mytabs.app
// (mytabs-customer-website src/views/Discover/useDiscoverTheme.js).
//
// Starts dark, like mytabs.app. Links from mytabs.app carry `?theme=light|dark`
// so the visitor keeps the theme they were using there; that wins over a saved
// pick. The visitor's pick is kept in the `tabs_theme` cookie for a year once
// they accept all cookies (see cookies.js); with "Necessary only" it lasts for
// the visit. Changes are broadcast so every component using this
// hook stays in sync (the top-bar toggles, the cookie banner, the MUI theme),
// and mirrored onto <html data-theme> where theme.css switches the tokens.

import { useCallback, useEffect, useState } from "react";

import { THEME_COOKIE, CONSENT_CHANGE_EVENT, getCookie, setCookie, deleteCookie, preferencesAllowed } from "./cookies";

const CHANGE_EVENT = "tabs-theme-change";

// The theme for this visit when it isn't saved in a cookie.
let sessionTheme = null;

const DEFAULT_THEME = "dark";
const THEME_PARAM = "theme";

const isTheme = (v) => v === "light" || v === "dark";

/**
 * Takes `?theme=light|dark` off the URL (public/index.html usually already
 * did, before first paint, and left it on window.__tabsThemeParam), keeps it
 * for the visit, saves it when cookies are allowed, and returns it.
 */
export function consumeThemeParam() {
  let theme = null;
  if (isTheme(window.__tabsThemeParam)) {
    theme = window.__tabsThemeParam;
    delete window.__tabsThemeParam;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.has(THEME_PARAM)) {
    const v = params.get(THEME_PARAM);
    if (isTheme(v)) theme = v;
    params.delete(THEME_PARAM);
    const search = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`
    );
  }
  if (!theme) return null;
  sessionTheme = theme;
  if (preferencesAllowed()) setCookie(THEME_COOKIE, theme);
  return theme;
}

export function readInitialTheme() {
  if (typeof window === "undefined") return DEFAULT_THEME;
  const fromLink = consumeThemeParam();
  if (fromLink) return fromLink;
  if (sessionTheme) return sessionTheme;
  const saved = getCookie(THEME_COOKIE);
  if (isTheme(saved)) return saved;
  return DEFAULT_THEME;
}

export function applyTheme(theme) {
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.style.colorScheme = theme;
}

/** Test helper: forget the per-visit theme. */
export function resetSessionTheme() {
  sessionTheme = null;
}

export default function useTheme() {
  const [theme, setTheme] = useState(readInitialTheme);

  // Follow toggles made by other components on the page.
  useEffect(() => {
    const onChange = (e) => setTheme(e.detail);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);

  // When the visitor accepts all cookies, save the theme they're using now.
  useEffect(() => {
    const onConsent = (e) => {
      if (e.detail === "all") setCookie(THEME_COOKIE, theme);
    };
    window.addEventListener(CONSENT_CHANGE_EVENT, onConsent);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onConsent);
  }, [theme]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    sessionTheme = next;
    if (preferencesAllowed()) setCookie(THEME_COOKIE, next);
    else deleteCookie(THEME_COOKIE);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  }, [theme]);

  return { theme, toggle };
}
